import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import authService from '../services/authService';
import foundationService from '../services/foundationService';
import { TextWithTooltips } from '../components/Tooltip';
import './ViewFoundation.css';
import BackToDashboard from '../components/BackToDashboard';

function ViewFoundation() {
  const navigate = useNavigate();
  const [foundation, setFoundation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCoreValuesTranscript, setShowCoreValuesTranscript] = useState(false);
  const [coreValuesConversation, setCoreValuesConversation] = useState([]);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load foundation data
    const result = await foundationService.getCompleteFoundation();

    if (result.success) {
      setFoundation(result.data.foundation);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const loadCoreValuesConversation = async () => {
    try {
      const response = await fetch('/api/generation/conversation/core_values', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCoreValuesConversation(data.conversation || []);
        setShowCoreValuesTranscript(true);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your foundation...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/dashboard/foundation')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <>
      <div className="no-print">
        <Container className="py-4">
          <BackToDashboard module="foundation" />
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Your Business Foundation</h1>
                <div>
                  <Button variant="primary" onClick={handlePrint} className="me-2">
                    Print Foundation
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="foundation-content py-4">
        {/* Business Basics */}
        <Card className="mb-4 print-section">
          <Card.Body>
            <h2 className="section-title">Business Basics</h2>

            {foundation?.business_name && (
              <div className="mb-3">
                <strong>Business Name:</strong>
                <p className="mb-0">{foundation.business_name}</p>
              </div>
            )}

            {foundation?.owner_name && (
              <div className="mb-3">
                <strong>Owner Name:</strong>
                <p className="mb-0">{foundation.owner_name}</p>
              </div>
            )}

            {foundation?.industry && (
              <div className="mb-3">
                <strong>Industry:</strong>
                <p className="mb-0">{foundation.industry}</p>
              </div>
            )}

            {foundation?.business_scope && (
              <div className="mb-3">
                <strong>Business Scope:</strong>
                <p className="mb-0">{foundation.business_scope}</p>
              </div>
            )}

            {foundation?.primary_location && (
              <div className="mb-3">
                <strong>Primary Location:</strong>
                <p className="mb-0">{foundation.primary_location}</p>
              </div>
            )}

            {foundation?.core_product && (
              <div className="mb-3">
                <strong>Core Product/Service:</strong>
                <p className="mb-0">{foundation.core_product}</p>
              </div>
            )}

            {foundation?.features_benefits && (
              <div className="mb-3">
                <strong>Features & Benefits:</strong>
                {(() => {
                  try {
                    const features = typeof foundation.features_benefits === 'string'
                      ? JSON.parse(foundation.features_benefits)
                      : foundation.features_benefits;
                    if (Array.isArray(features)) {
                      return (
                        <ul className="mb-0 mt-1">
                          {features.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      );
                    }
                    return <p className="mb-0 preserve-whitespace">{foundation.features_benefits}</p>;
                  } catch {
                    return <p className="mb-0 preserve-whitespace">{foundation.features_benefits}</p>;
                  }
                })()}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Core Values */}
        {foundation?.core_values && foundation.core_values.length > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Core Values" /></h2>
              {foundation.core_values.map((value, index) => (
                <div key={index} className="mb-3">
                  <strong>{value.value}</strong>
                  <p className="mb-0 text-muted">{value.explanation}</p>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}

        {/* Core Values Discovery Conversation - hidden from print */}
        <Card className="mb-4 no-print border-info">
          <Card.Header
            className="bg-info text-white d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => showCoreValuesTranscript ? setShowCoreValuesTranscript(false) : loadCoreValuesConversation()}
          >
            <strong>💬 Core Values Discovery Conversation</strong>
            <small>{showCoreValuesTranscript ? '(click to hide)' : '(click to view)'}</small>
          </Card.Header>
          {showCoreValuesTranscript && (
            <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <Alert variant="light" className="mb-3">
                <strong>💡 Content Gold Mine!</strong> This conversation contains your authentic voice, real stories,
                and AI reframes. Great for sales pages, about pages, social posts, and any content that needs to feel genuine.
              </Alert>
              {coreValuesConversation.length > 0 ? (
                coreValuesConversation.map((msg, index) => (
                  <div
                    key={index}
                    className={`mb-3 p-3 rounded ${msg.role === 'assistant' ? 'bg-light' : 'bg-white border'}`}
                  >
                    <strong className={msg.role === 'assistant' ? 'text-primary' : 'text-success'}>
                      {msg.role === 'assistant' ? '🤖 AI Coach:' : '👤 You:'}
                    </strong>
                    <div className="mt-2"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                  </div>
                ))
              ) : (
                <p className="text-muted">No conversation found. Complete the Core Values Discovery to see your transcript here.</p>
              )}
            </Card.Body>
          )}
        </Card>

        {/* Mission Statement */}
        {foundation?.mission_statement && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Mission Statement" /></h2>
              <p className="preserve-whitespace">{foundation.mission_statement}</p>
            </Card.Body>
          </Card>
        )}

        {/* Vision Statement */}
        {foundation?.vision_statement && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Vision Statement" /></h2>
              <p className="preserve-whitespace">{foundation.vision_statement}</p>
            </Card.Body>
          </Card>
        )}

        {/* Target Market */}
        {foundation?.primary_niche && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Primary <TextWithTooltips text="Target Market" /></h2>

              <div className="mb-3">
                <strong>Market <TextWithTooltips text="Niche" />:</strong>
                <p className="mb-0">{foundation.primary_niche.name}</p>
              </div>

              {foundation.primary_niche.description && (
                <div className="mb-3">
                  <strong>Description:</strong>
                  <p className="mb-0 preserve-whitespace">{foundation.primary_niche.description}</p>
                </div>
              )}

              {foundation.primary_niche.pain_points && (
                <div className="mb-3">
                  <strong><TextWithTooltips text="Pain Point" />s:</strong>
                  <p className="mb-0 preserve-whitespace">{foundation.primary_niche.pain_points}</p>
                </div>
              )}

              {foundation.primary_niche.why_they_convert && (
                <div className="mb-3">
                  <strong>Why They Convert:</strong>
                  <p className="mb-0 preserve-whitespace">{foundation.primary_niche.why_they_convert}</p>
                </div>
              )}

              {foundation.primary_niche.market_size && (
                <div className="mb-3">
                  <strong><TextWithTooltips text="Market Size" />:</strong>
                  <p className="mb-0">{foundation.primary_niche.market_size}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Customer Personas */}
        {foundation?.customer_personas && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Customer Journey" /> & <TextWithTooltips text="Persona" />s</h2>
              <div className="persona-text">
                <ReactMarkdown>{foundation.customer_personas}</ReactMarkdown>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Capacity Assessment */}
        {foundation?.capacity_data && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Capacity Assessment" /></h2>

              <Row>
                <Col md={4} className="mb-3">
                  <strong>Current Capacity:</strong>
                  <p className="mb-0">{foundation.capacity_data.current_customers_per_month} customers/month</p>
                </Col>
                <Col md={4} className="mb-3">
                  <strong>Additional Capacity:</strong>
                  <p className="mb-0">{foundation.capacity_data.additional_capacity} more customers/month</p>
                </Col>
                <Col md={4} className="mb-3">
                  <strong>Max Capacity:</strong>
                  <p className="mb-0">{foundation.capacity_data.max_capacity} customers/month</p>
                </Col>
              </Row>

              {foundation.capacity_data.scale_speed && (
                <div className="mb-3">
                  <strong>Scaling Speed:</strong>
                  <p className="mb-0 text-capitalize">{foundation.capacity_data.scale_speed.replace(/_/g, ' ')}</p>
                </div>
              )}

              {foundation.capacity_data.scaling_constraints && foundation.capacity_data.scaling_constraints.length > 0 && (
                <div className="mb-0">
                  <strong><TextWithTooltips text="Scaling Constraints" />:</strong>
                  <ul className="mb-0 mt-1">
                    {foundation.capacity_data.scaling_constraints.map((constraint, idx) => (
                      <li key={idx} className="text-capitalize">{constraint.replace(/_/g, ' ')}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Results & Outcomes */}
        {foundation?.results_outcomes && foundation.results_outcomes.length > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Results & Outcomes" /></h2>
              <p className="text-muted mb-3">
                The specific, measurable results your customers achieve:
              </p>
              <ul>
                {foundation.results_outcomes.map((result, index) => (
                  <li key={index} className="mb-2">{result}</li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        )}

        {/* Exit Strategy */}
        {foundation?.exit_strategy && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title"><TextWithTooltips text="Exit Strategy" /> & Long-Term Vision</h2>

              <div className="mb-3">
                <strong><TextWithTooltips text="Exit Path" />:</strong>
                <p className="mb-0 text-capitalize">{foundation.exit_strategy.replace(/_/g, ' ')}</p>
              </div>

              {foundation.exit_strategy_analysis && (
                <div>
                  <strong>Strategic Analysis:</strong>
                  <div className="mt-2"><ReactMarkdown>{foundation.exit_strategy_analysis}</ReactMarkdown></div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <div className="no-print mt-4 mb-4">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            ← Back to Dashboard
          </Button>
        </div>
      </Container>
    </>
  );
}

export default ViewFoundation;
